import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, CreditCard, ArrowRight, Loader2, Calendar, Tag } from "lucide-react";
import { toast } from "sonner";

const SERVICE_NAMES = {
  "ai-quick-scan": "AI Quick Scan",
  "ai-pilot": "AI Pilot",
  "ai-workflow": "AI Workflow",
  "ai-training": "AI Training",
  "ai-governance": "AI Governance",
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [reports, setReports] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bundles, setBundles] = useState([
    { id: "starter", services: ["ai-quick-scan", "ai-training"], original_price: 5900, price: 5400, savings: 500, name: "Starter", description: "Quick Scan + Training" },
    { id: "growth", services: ["ai-quick-scan", "ai-pilot", "ai-training"], original_price: 10850, price: 9850, savings: 1000, name: "Growth", description: "Scan + Pilot + Training" },
    { id: "enterprise", services: ["ai-quick-scan", "ai-pilot", "ai-workflow", "ai-training", "ai-governance"], original_price: 27750, price: 22750, savings: 5000, name: "Enterprise", description: "All 5 services" },
    { id: "compliance", services: ["ai-governance", "ai-training"], original_price: 10900, price: 9400, savings: 1500, name: "Compliance", description: "Governance + Training" },
  ]);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    API.get(`/bundles?lang=${lang}`).then(res => { if (Array.isArray(res.data) && res.data.length > 0) setBundles(res.data); }).catch(() => {});
  }, [lang]);

  const loadData = async () => {
    try {
      const [intakesRes, reportsRes, bookingsRes] = await Promise.all([
        API.get("/intake"),
        API.get("/reports"),
        API.get("/bookings"),
      ]);
      setIntakes(intakesRes.data);
      setReports(reportsRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async (intake) => {
    try {
      const { data } = await API.post("/payments/checkout", {
        service_id: intake.service_id,
        origin_url: window.location.origin,
        intake_id: intake.intake_id,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Payment error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleBundlePurchase = async (bundleId) => {
    try {
      const { data } = await API.post("/payments/bundle-checkout", {
        bundle_id: bundleId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleGenerateReport = async (intakeId) => {
    setGeneratingId(intakeId);
    try {
      const { data } = await API.post(`/reports/generate/${intakeId}`);
      toast.success(lang === "nl" ? "Rapport gegenereerd!" : "Report generated!");
      setReports((prev) => [data, ...prev]);
      setIntakes((prev) =>
        prev.map((i) => (i.intake_id === intakeId ? { ...i, report_status: "completed" } : i))
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error generating report");
    } finally {
      setGeneratingId(null);
    }
  };

  const services = [
    { id: "ai-quick-scan", name: "AI Quick Scan" },
    { id: "ai-pilot", name: "AI Pilot" },
    { id: "ai-workflow", name: lang === "nl" ? "AI Workflow Automatisering" : "AI Workflow Automation" },
    { id: "ai-training", name: "AI Training" },
    { id: "ai-governance", name: "AI Governance" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-6 h-6 border-2 border-[#0A3FA3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB]" data-testid="client-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="dashboard-title">{t("dashboard.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("dashboard.welcome")}, {user?.name || user?.email}</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-border rounded-sm">
            <TabsTrigger value="overview" className="rounded-sm text-xs" data-testid="tab-overview">{t("dashboard.tab_overview")}</TabsTrigger>
            <TabsTrigger value="intakes" className="rounded-sm text-xs" data-testid="tab-intakes">{t("dashboard.tab_intakes")}</TabsTrigger>
            <TabsTrigger value="reports" className="rounded-sm text-xs" data-testid="tab-reports">{t("dashboard.tab_reports")}</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-sm text-xs" data-testid="tab-bookings">{lang === "nl" ? "Afspraken" : lang === "de" ? "Termine" : lang === "fr" ? "Rendez-vous" : "Bookings"}</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4">{t("dashboard.choose_service")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((s) => (
                  <Link key={s.id} to={`/intake/${s.id}`} className="group block p-6 bg-white border border-border hover:border-[#0A3FA3] transition-colors" data-testid={`dashboard-service-${s.id}`}>
                    <h3 className="font-bold mb-1 group-hover:text-[#0A3FA3] transition-colors">{s.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-[#0A3FA3] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      {t("services.start_intake")} <ArrowRight size={14} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            {/* Bundles upsell */}
            {bundles.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-bold mb-2">
                  <Tag size={16} className="inline mr-2 text-[#FF3B30]" />
                  {({nl:"Bespaar met bundels",en:"Save with bundles",de:"Sparen mit Paketen",fr:"\u00c9conomisez avec nos packs",es:"Ahorre con paquetes",ar:"\u0648\u0641\u0631 \u0645\u0639 \u0627\u0644\u062d\u0632\u0645"})[lang] || "Save with bundles"}
                </h2>
                <p className="text-xs text-muted-foreground mb-4">{({nl:"Combineer diensten en bespaar tot \u20ac5.000",en:"Combine services and save up to \u20ac5,000",de:"Dienste kombinieren und bis zu \u20ac5.000 sparen",fr:"Combinez les services et \u00e9conomisez jusqu'\u00e0 5 000\u20ac",es:"Combine servicios y ahorre hasta \u20ac5.000",ar:"\u0627\u062c\u0645\u0639 \u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0648\u0648\u0641\u0631 \u062d\u062a\u0649 5000\u20ac"})[lang] || "Combine services and save up to \u20ac5,000"}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {bundles.map((b) => (
                    <div key={b.id} className="bg-white border border-border p-4 flex items-center justify-between hover:border-[#FF3B30] transition-colors" data-testid={`dashboard-bundle-${b.id}`}>
                      <div>
                        <div className="font-bold text-sm">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.description}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground line-through">&euro;{b.original_price.toLocaleString()}</span>
                          <span className="text-sm font-bold text-[#0A3FA3]">&euro;{b.price.toLocaleString()}</span>
                          <span className="text-[10px] font-bold text-[#FF3B30] bg-[#FF3B30]/10 px-1.5 py-0.5">{({nl:"BESPAAR",en:"SAVE",de:"SPAREN",fr:"\u00c9CONOMIE",es:"AHORRO",ar:"\u0648\u0641\u0631"})[lang] || "SAVE"} &euro;{b.savings.toLocaleString()}</span>
                        </div>
                      </div>
                      <Button size="sm" className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm text-xs flex-shrink-0" onClick={() => handleBundlePurchase(b.id)} data-testid={`buy-bundle-${b.id}`}>
                        <CreditCard size={14} className="mr-1" /> {({nl:"Kopen",en:"Buy",de:"Kaufen",fr:"Acheter",es:"Comprar",ar:"\u0634\u0631\u0627\u0621"})[lang] || "Buy"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {intakes.length > 0 && (
              <div>
                <h2 className="text-lg font-bold mb-4">{t("dashboard.tab_intakes")}</h2>
                <div className="space-y-3">
                  {intakes.slice(0, 5).map((intake) => (
                    <IntakeRow key={intake.intake_id} intake={intake} t={t} onPay={handlePay} onGenerate={handleGenerateReport} generatingId={generatingId} navigate={navigate} reports={reports} />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Intakes */}
          <TabsContent value="intakes">
            {intakes.length === 0 ? (
              <div className="bg-white border border-border p-12 text-center">
                <p className="text-muted-foreground" data-testid="no-intakes">{t("dashboard.no_intakes")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {intakes.map((intake) => (
                  <IntakeRow key={intake.intake_id} intake={intake} t={t} onPay={handlePay} onGenerate={handleGenerateReport} generatingId={generatingId} navigate={navigate} reports={reports} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports">
            {reports.length === 0 ? (
              <div className="bg-white border border-border p-12 text-center">
                <p className="text-muted-foreground" data-testid="no-reports">{t("dashboard.no_reports")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map((report) => (
                  <div key={report.report_id} className="bg-white border border-border p-4 flex items-center justify-between" data-testid={`report-row-${report.report_id}`}>
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-[#0A3FA3]" />
                      <div>
                        <span className="font-medium text-sm">{SERVICE_NAMES[report.service_id] || report.service_id}</span>
                        <div className="text-xs text-muted-foreground">{new Date(report.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <Link to={`/reports/${report.report_id}`}>
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid={`view-report-${report.report_id}`}>
                        {t("dashboard.view_report")}
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings">
            <BookingSection bookings={bookings} lang={lang} onRefresh={loadData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function IntakeRow({ intake, t, onPay, onGenerate, generatingId, navigate, reports }) {
  const isPaid = intake.payment_status === "paid";
  const hasReport = intake.report_status === "completed" || reports?.some((r) => r.intake_id === intake.intake_id);
  const reportObj = reports?.find((r) => r.intake_id === intake.intake_id);

  return (
    <div className="bg-white border border-border p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`intake-row-${intake.intake_id}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm">{SERVICE_NAMES[intake.service_id] || intake.service_id}</span>
          <Badge variant={isPaid ? "default" : "secondary"} className="text-[10px] rounded-sm" data-testid={`intake-payment-badge-${intake.intake_id}`}>
            {isPaid ? t("dashboard.paid") : t("dashboard.pending")}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">{new Date(intake.created_at).toLocaleDateString()}</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!isPaid && (
          <Button size="sm" className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm text-xs" onClick={() => onPay(intake)} data-testid={`pay-btn-${intake.intake_id}`}>
            <CreditCard size={14} className="mr-1" /> {t("dashboard.pay_now")}
          </Button>
        )}
        {isPaid && !hasReport && (
          <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => onGenerate(intake.intake_id)} disabled={generatingId === intake.intake_id} data-testid={`generate-report-btn-${intake.intake_id}`}>
            {generatingId === intake.intake_id ? <><Loader2 size={14} className="mr-1 animate-spin" />{t("dashboard.generating")}</> : <><FileText size={14} className="mr-1" />{t("dashboard.generate_report")}</>}
          </Button>
        )}
        {hasReport && reportObj && (
          <Link to={`/reports/${reportObj.report_id}`}>
            <Button size="sm" variant="outline" className="rounded-sm text-xs text-[#0A3FA3] border-[#0A3FA3]" data-testid={`view-report-btn-${intake.intake_id}`}>
              <FileText size={14} className="mr-1" /> {t("dashboard.view_report")}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}


function BookingSection({ bookings, lang, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ service_id: "ai-quick-scan", preferred_date: "", preferred_time: "10:00", booking_type: "video", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const labels = {
    nl: { title: "Afspraken", new: "Nieuwe afspraak", service: "Dienst", date: "Datum", time: "Tijd", type: "Type", notes: "Opmerkingen", submit: "Afspraak aanvragen", cancel: "Annuleren", no_bookings: "Geen afspraken gepland.", status: { requested: "Aangevraagd", confirmed: "Bevestigd", cancelled: "Geannuleerd", completed: "Voltooid" }, types: { video: "Videogesprek", phone: "Telefonisch", onsite: "Op locatie" } },
    en: { title: "Bookings", new: "New booking", service: "Service", date: "Date", time: "Time", type: "Type", notes: "Notes", submit: "Request booking", cancel: "Cancel", no_bookings: "No bookings scheduled.", status: { requested: "Requested", confirmed: "Confirmed", cancelled: "Cancelled", completed: "Completed" }, types: { video: "Video call", phone: "Phone", onsite: "On-site" } },
    de: { title: "Termine", new: "Neuer Termin", service: "Service", date: "Datum", time: "Zeit", type: "Typ", notes: "Anmerkungen", submit: "Termin anfragen", cancel: "Abbrechen", no_bookings: "Keine Termine geplant.", status: { requested: "Angefragt", confirmed: "Best\u00e4tigt", cancelled: "Storniert", completed: "Abgeschlossen" }, types: { video: "Videoanruf", phone: "Telefon", onsite: "Vor Ort" } },
    fr: { title: "Rendez-vous", new: "Nouveau rendez-vous", service: "Service", date: "Date", time: "Heure", type: "Type", notes: "Notes", submit: "Demander un rendez-vous", cancel: "Annuler", no_bookings: "Aucun rendez-vous pr\u00e9vu.", status: { requested: "Demand\u00e9", confirmed: "Confirm\u00e9", cancelled: "Annul\u00e9", completed: "Termin\u00e9" }, types: { video: "Appel vid\u00e9o", phone: "T\u00e9l\u00e9phone", onsite: "Sur site" } },
  };
  const t = labels[lang] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await API.post("/bookings", formData);
      toast.success(lang === "nl" ? "Afspraak aangevraagd!" : "Booking requested!");
      setShowForm(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const statusColors = { requested: "bg-yellow-50 text-yellow-700", confirmed: "bg-green-50 text-green-700", cancelled: "bg-red-50 text-red-700", completed: "bg-blue-50 text-blue-700" };

  return (
    <div data-testid="bookings-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">{t.title}</h2>
        <Button size="sm" className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm text-xs" onClick={() => setShowForm(!showForm)} data-testid="new-booking-btn">
          <Calendar size={14} className="mr-1" /> {t.new}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border p-6 mb-4" data-testid="booking-form">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider block mb-1">{t.service}</label>
              <select value={formData.service_id} onChange={(e) => setFormData({ ...formData, service_id: e.target.value })} className="w-full border border-border rounded-sm p-2 text-sm" data-testid="booking-service-select">
                {Object.entries(SERVICE_NAMES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider block mb-1">{t.date}</label>
              <input type="date" value={formData.preferred_date} onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })} required min={new Date().toISOString().split("T")[0]} className="w-full border border-border rounded-sm p-2 text-sm" data-testid="booking-date-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider block mb-1">{t.time}</label>
              <select value={formData.preferred_time} onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })} className="w-full border border-border rounded-sm p-2 text-sm" data-testid="booking-time-select">
                {["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider block mb-1">{t.type}</label>
              <select value={formData.booking_type} onChange={(e) => setFormData({ ...formData, booking_type: e.target.value })} className="w-full border border-border rounded-sm p-2 text-sm" data-testid="booking-type-select">
                <option value="video">{t.types.video}</option>
                <option value="phone">{t.types.phone}</option>
                <option value="onsite">{t.types.onsite}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider block mb-1">{t.notes}</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full border border-border rounded-sm p-2 text-sm resize-none" data-testid="booking-notes-input" />
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" size="sm" className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm text-xs" disabled={submitting} data-testid="booking-submit-btn">{submitting ? "..." : t.submit}</Button>
              <Button type="button" size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setShowForm(false)}>{t.cancel}</Button>
            </div>
          </form>
        </div>
      )}

      {bookings.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center"><p className="text-muted-foreground">{t.no_bookings}</p></div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.booking_id} className="bg-white border border-border p-4 flex items-center justify-between" data-testid={`booking-row-${b.booking_id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm">{SERVICE_NAMES[b.service_id] || b.service_id}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${statusColors[b.status] || "bg-gray-100"}`}>{t.status[b.status] || b.status}</span>
                </div>
                <div className="text-xs text-muted-foreground">{b.preferred_date} {b.preferred_time} - {t.types[b.booking_type] || b.booking_type}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
